import { MailCheck } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { flushSync } from "react-dom";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useAnmelden, useIch, useLinkAnfordern, useRegistrieren } from "../api/abfragen";
import { linkFehlerText } from "../api/client";
import { registriertAufraeumen, registriertGleich, registriertMerken } from "../api/registriert";
import { Feld } from "../design/Feld";
import { Knopf } from "../design/Knopf";
import { Einwilligung, RechtlichesLinks } from "./rechtliches/Rechtliches";
import { FASSUNG } from "./rechtliches/texte";
import { Segment } from "../design/Segment";
import { AnbieterKnoepfe } from "./anbieter/AnbieterKnoepfe";
import { Geist } from "../welt/Geist";
import { WeltWurzel } from "../welt/WeltWurzel";

type Modus = "anmelden" | "registrieren";
/** W20 (D-063): der Link ist der Standardweg, das Passwort der stille zweite. */
type Weg = "link" | "passwort";

/** Wartezeit, bevor sich derselbe Link noch einmal anfordern laesst. */
const NOCHMAL_NACH_S = 30;

/** Anmeldung und Registrierung auf einer Seite (Segment). Klartext, kurze Verben. W73d (D-344, D-362): wie iOS W73-I-b
 *  kuerzer: „E-Mail“ und „Weiter“, nach dem Senden „Wir haben dir eine E-Mail zum Anmelden geschickt.“, klein
 *  „Stattdessen mit Passwort anmelden“ vorne und nach dem Senden, „Stattdessen Link schicken“ zurueck. */
export function Anmelden() {
  const ort = useLocation();
  const zustand = ort.state as { von?: string; email?: string } | null;
  const [modus, setModus] = useState<Modus>("anmelden");
  const [weg, setWeg] = useState<Weg>("link");
  // „Neuen Link schicken“ von der Seite des Links bringt die Adresse mit, wenn sie sie kennt
  const [email, setEmail] = useState(zustand?.email ?? "");
  const [passwort, setPasswort] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [einwilligung, setEinwilligung] = useState(false);
  const [nutzung, setNutzung] = useState(true);
  // W82 Web-5b (K4): nach „Konto anlegen“ (und beim zu frühen Link für dieselbe Adresse) der Zustand „Bestätige deine Adresse“
  const [bestaetige, setBestaetige] = useState<{ adresse: string; meldung: string | null } | null>(null);
  // Adresse, an die der Link ging: dann zeigt die Seite „Wir haben dir eine E-Mail …“ (solange der Weg der Link ist)
  const [unterwegs, setUnterwegs] = useState<string | null>(null);
  // W73d (E7): Bremse fuer „Noch mal schicken“ im Zustand der Seite, nicht in der Unteransicht: Passwort auf- und
  // zuklappen setzt sie weder zurueck noch verkuerzt sie; jedes erfolgreiche Senden startet sie neu
  const [gesendetAm, setGesendetAm] = useState(0);
  const [jetzt, setJetzt] = useState(() => Date.now());
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null);
  const anmelden = useAnmelden();
  const registrieren = useRegistrieren();
  const link = useLinkAnfordern();
  const { data: ich } = useIch();
  const navigieren = useNavigate();
  const linkHinweis = useId();
  const emailFeld = useRef<HTMLInputElement>(null);
  const passwortFeld = useRef<HTMLInputElement>(null);
  // Doppeltipp und Enter senden einmal: `isPending` kommt erst nach dem naechsten Takt an, die Sperre gilt sofort
  const belegt = useRef(false);
  const ziel = zustand?.von ?? "/";
  // W73e (S7, D-382 H2, Entscheid E12): auch solange Apple oder Google startet, ist jeder Knopf der Seite gesperrt (wie iOS)
  const [anbieterLaeuft, setAnbieterLaeuft] = useState(false);
  const laeuft = anmelden.isPending || registrieren.isPending || link.isPending || anbieterLaeuft;
  const fehler = modus === "registrieren" ? registrieren.error : weg === "link" ? link.error : anmelden.error;
  const rest = Math.max(0, Math.ceil((gesendetAm + NOCHMAL_NACH_S * 1000 - jetzt) / 1000));

  useEffect(() => {
    if (rest === 0) return;
    const uhr = setInterval(() => setJetzt(Date.now()), 1000);
    return () => clearInterval(uhr);
  }, [rest]);
  // R1 W82-Web-5b: ein abgelaufener Merker `pg-registriert` bleibt nicht liegen
  useEffect(() => registriertAufraeumen(), []);

  if (ich) return <Navigate to={ziel} replace />;

  /** Link anfordern (Weiter und Noch mal); der neue Zustand erst nach der Antwort 202. */
  function linkSchicken(adresse: string, nochmal: boolean) {
    if (belegt.current) return;
    belegt.current = true;
    if (nochmal) setMeldung(null);
    link.mutate(adresse, {
      onSuccess: () => {
        // K4 (entscheide C3): nur der Merker dieses Browsers unterscheidet, nie die Antwort (immer 202, W13b); der Server
        // schickt in diesem Fall die Hinweis-Mail
        if (registriertGleich(adresse)) {
          setUnterwegs(null);
          setMeldung(null);
          setBestaetige({ adresse, meldung: "Deine Adresse ist noch nicht bestätigt." });
          return;
        }
        setUnterwegs(adresse);
        setGesendetAm(Date.now());
        setJetzt(Date.now());
        setMeldung(nochmal ? { text: "Wir haben den Link noch einmal geschickt.", fehler: false } : null);
      },
      onError: (f) => nochmal && setMeldung({ text: linkFehlerText(f), fehler: true }),
      onSettled: () => {
        belegt.current = false;
      },
    });
  }

  function absenden(e: FormEvent) {
    e.preventDefault();
    if (laeuft) return;
    if (modus === "anmelden" && weg === "link") {
      linkSchicken(email.trim(), false);
      return;
    }
    if (modus === "anmelden") {
      if (belegt.current) return;
      belegt.current = true;
      anmelden.mutate(
        { email, passwort },
        { onSuccess: () => navigieren(ziel, { replace: true }), onSettled: () => (belegt.current = false) },
      );
      return;
    }
    // W13b: Die Antwort verrät nicht, ob es die Adresse schon gab; danach meldet man sich an.
    // W56 (F64 = A): ohne Passwort, angemeldet wird per Link; ein Passwort legt man danach in den Einstellungen fest
    registrieren.mutate(
      {
        email,
        name,
        haushalt: `Haushalt ${name.split(" ")[0] ?? name}`,
        einladungscode: code,
        einwilligung,
        fassung: FASSUNG,
        nutzung_aus: !nutzung,
      },
      {
        onSuccess: () => {
          registriertMerken(email);
          // Name, Code und Haken bleiben: „Andere Adresse“ führt zurück ins Formular, nur die Adresse ist neu
          setModus("anmelden");
          setWeg("link");
          setUnterwegs(null);
          setBestaetige({ adresse: email.trim(), meldung: null });
        },
      },
    );
  }

  /** „Stattdessen mit Passwort anmelden“ (vor oder nach dem Senden): unter derselben Adresse; der Fokus geht noch im Klick
   *  ins Passwortfeld, sonst oeffnet Safari keine Tastatur (iOS `passwortAufklappen`). */
  function passwortAufklappen() {
    flushSync(() => {
      if (unterwegs !== null) setEmail(unterwegs);
      setWeg("passwort");
      setPasswort("");
      anmelden.reset();
      link.reset();
    });
    passwortFeld.current?.focus();
  }

  /** „Stattdessen Link schicken“: Passwort und Fehler weg; zurueck zum gesendeten Zustand, wenn ein Link unterwegs ist
   *  (Fokus auf dessen Ueberschrift, LinkUnterwegs), sonst zum Formular mit „Weiter“ (Fokus ins Feld „E-Mail“). */
  function linkStattPasswort() {
    flushSync(() => {
      setWeg("link");
      setPasswort("");
      anmelden.reset();
      link.reset();
    });
    if (unterwegs === null) emailFeld.current?.focus();
  }

  return (
    // Handoff PgSignIn: mobil Marke oben, darunter Segment und Formular; am Desktop links die Marke mit
    // Satz (ohne die Blatt-Illustration: keine Papier-Metaphern), rechts das Formular als Karte.
    // W82 Teil 4 (Vorlage R11 „anmelden“, FW14, F91): der Geist als Zeichen über dem Namen; bei 100 % fest, die Figur
    // nimmt den Platz, der übrig bleibt (welt/rest.css `.anmelden-w82`), ab 150 % rollt die Seite
    <WeltWurzel kennung="anmelden-seite">
    <main className="anmelden-w82 rand mx-auto flex min-h-dvh max-w-(--w-readable) flex-col justify-center lg:grid lg:max-w-(--w-breit) lg:grid-cols-2 lg:items-center lg:gap-16">
      {/* Web-5d K1: ab 1024 px links der Geist mit Namen, Satz und Text, rechts die Karte; darunter ohne eigenen Kasten
          (welt/rest.css `.anmelden-marke`), die Reihenfolge bleibt Figur, Name, Satz, Formular */}
      <div className="anmelden-marke">
        <div className="marke-block flex flex-col items-center text-center lg:items-start lg:text-left">
          <span className="marke-platz" aria-hidden="true">
            <Geist />
          </span>
          <h1 className="t-large-title marke-titel">Papiergaist</h1>
          <p className="t-subheadline marke-satz text-pg-text-2 lg:hidden">Deine Post, sortiert und auffindbar.</p>
        </div>
        <aside aria-hidden="true" className="hidden lg:block">
          <p className="t-title-2 max-w-[20ch] text-balance">Deine Post, sortiert und auffindbar.</p>
          <p className="t-body mt-3 max-w-[36ch] text-pg-text-2">
            Briefe scannen oder per Mail empfangen. Papiergaist liest, ordnet ein und behält Fristen und Zahlungen im Blick.
          </p>
        </aside>
      </div>
      <div className="anmelden-karte">
      {bestaetige !== null ? (
        <BestaetigeAdresse
          adresse={bestaetige.adresse}
          meldung={bestaetige.meldung}
          onAndereAdresse={() => {
            setBestaetige(null);
            setModus("registrieren");
            registrieren.reset();
            link.reset();
            // zurück ins Formular „Konto anlegen“, gleich ins Feld „E-Mail“
            requestAnimationFrame(() => emailFeld.current?.focus());
          }}
          onZurAnmeldung={() => {
            setBestaetige(null);
            setModus("anmelden");
            setWeg("link");
            setEmail(bestaetige.adresse);
            link.reset();
            requestAnimationFrame(() => emailFeld.current?.focus());
          }}
        />
      ) : modus === "anmelden" && weg === "link" && unterwegs !== null ? (
        <LinkUnterwegs
          adresse={unterwegs}
          rest={rest}
          meldung={meldung}
          laeuft={laeuft}
          onNochmal={() => linkSchicken(unterwegs, true)}
          onPasswort={passwortAufklappen}
          onAndereAdresse={() => {
            setUnterwegs(null);
            setMeldung(null);
            link.reset();
            // nach dem Wechsel zurueck ins Feld, damit die Adresse gleich geaendert werden kann
            requestAnimationFrame(() => emailFeld.current?.focus());
          }}
        />
      ) : (
        <>
          <Segment
            label="Anmelden oder Konto anlegen"
            wert={modus}
            onChange={setModus}
            gesperrt={laeuft}
            optionen={[
              { wert: "anmelden", label: "Anmelden" },
              { wert: "registrieren", label: "Konto anlegen" },
            ]}
          />
          {/* W68: Apple und Google ueber dem Formular, in beiden Segmenten; ohne eingerichteten Anbieter nichts */}
          <AnbieterKnoepfe von={ziel} registrieren={modus === "registrieren"} gesperrt={laeuft} onLaeuft={setAnbieterLaeuft} />
          <form onSubmit={absenden} className="anmelden-form">
            <ul className="gruppe overflow-clip rounded-group bg-pg-surface">
              {modus === "registrieren" && (
                <>
                  <Feld
                    label="Einladungscode"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  <Feld label="Name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
                </>
              )}
              <Feld
                ref={emailFeld}
                label="E-Mail"
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                aria-describedby={modus === "registrieren" ? linkHinweis : undefined}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {modus === "anmelden" && weg === "passwort" && (
                <Feld
                  ref={passwortFeld}
                  label="Passwort"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={passwort}
                  onChange={(e) => setPasswort(e.target.value)}
                />
              )}
            </ul>
            {modus === "registrieren" && (
              <p id={linkHinweis} className="t-footnote mt-2 px-4 text-pg-text-2">
                Du meldest dich per Link an. Ein Passwort kannst du danach in den Einstellungen festlegen.
              </p>
            )}
            {modus === "registrieren" && <Einwilligung wert={einwilligung} onChange={setEinwilligung} nutzung={nutzung} onNutzung={setNutzung} />}
            {modus === "registrieren" && (
              <p className="t-footnote mt-2 px-4 text-pg-text-2">
                Papiergaist ist in der Beta: Du brauchst einen Einladungscode. Dein Haushalt wird gleich mit angelegt.
              </p>
            )}
            {fehler && (
              <p role="alert" className="anmelde-fehler t-subheadline mt-4 px-4">
                {modus === "anmelden" && weg === "link" ? linkFehlerText(fehler) : fehler.message}
              </p>
            )}
            <Knopf type="submit" gross breit className="anmelden-weiter" disabled={laeuft}>
              {modus === "registrieren" ? "Konto anlegen" : weg === "link" ? "Weiter" : "Anmelden"}
            </Knopf>
          </form>
          {modus === "anmelden" && weg === "link" && (
            <p className="anmelden-wege text-center">
              <KleinerWeg disabled={laeuft} onClick={passwortAufklappen}>
                Stattdessen mit Passwort anmelden
              </KleinerWeg>
            </p>
          )}
          {modus === "anmelden" && weg === "passwort" && (
            <p className="anmelden-wege t-footnote flex flex-wrap justify-center gap-x-6 text-center">
              <KleinerWeg disabled={laeuft} onClick={linkStattPasswort}>
                Stattdessen Link schicken
              </KleinerWeg>
              <Link to="/passwort-vergessen" className="kleiner-weg inline-flex min-h-(--touch) items-center text-pg-accent-text hover:opacity-70">
                Passwort vergessen?
              </Link>
            </p>
          )}
        </>
      )}
      </div>
      <RechtlichesLinks className="anmelden-recht lg:col-span-2" />
    </main>
    </WeltWurzel>
  );
}

/** Kleiner Wechsel des Wegs (iOS `kleinerWeg`): Text in Akzentfarbe, eine Stufe kleiner als der Hauptknopf, Trefferflaeche
 *  mindestens 44 px; waehrend einer Anfrage gesperrt. */
function KleinerWeg(props: { onClick: () => void; disabled: boolean; children: string }) {
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      className="kleiner-weg t-footnote inline-flex min-h-(--touch) items-center text-pg-accent-text hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {props.children}
    </button>
  );
}

/** Die Adresse mit einer Umbruchstelle nach „@“ (nur in der Anzeige; vorgelesen ohne Zusatzzeichen). */
function Adresse(props: { adresse: string }) {
  const at = props.adresse.indexOf("@");
  if (at < 0 || at === props.adresse.length - 1) return <>{props.adresse}</>;
  return (
    <>
      {props.adresse.slice(0, at + 1)}
      <wbr />
      {props.adresse.slice(at + 1)}
    </>
  );
}

/** W82 Web-5b (K4, F2 = A): nach „Konto anlegen“ wie `LinkUnterwegs` ein Glas-Modul mit Linse, Überschrift (Fokus), Adresse
 *  mit Umbruch nach „@“, was jetzt zu tun ist, und den Wegen „Zur Anmeldung“ und „Andere Adresse“. Mit `meldung` (Link für
 *  dieselbe Adresse vor dem Bestätigen) steht sie über dem Satz. Wortlaut Satzquelle `auftrag-w82-web-5b.md`. */
function BestaetigeAdresse(props: { adresse: string; meldung: string | null; onAndereAdresse: () => void; onZurAnmeldung: () => void }) {
  const titel = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titel.current?.focus(); // der Inhalt wechselt: Fokus auf die neue Ueberschrift
  }, []);
  return (
    <section className="link-unterwegs bestaetige-adresse glas glas-modul flex flex-col items-center text-center" data-testid="bestaetige-adresse">
      <span className="linse-gross grid size-16 place-items-center rounded-group bg-pg-accent-soft text-pg-accent-text">
        <MailCheck size={32} strokeWidth={1.5} aria-hidden />
      </span>
      <h2 ref={titel} tabIndex={-1} className="t-title-2 mt-4 outline-none">
        Bestätige deine Adresse
      </h2>
      <p className="link-adresse t-headline mt-2 max-w-full">
        <Adresse adresse={props.adresse} />
      </p>
      {props.meldung && (
        <p role="status" className="t-subheadline mt-3 font-semibold">
          {props.meldung}
        </p>
      )}
      <p className="t-subheadline mt-2 text-pg-text-2">
        Wir haben dir eine <span className="whitespace-nowrap">E-Mail</span> geschickt. Öffne den Link darin, danach meldest du dich mit einem Tipp an.
      </p>
      <p className="t-subheadline mt-2 mb-6 text-pg-text-2">Schau auch im Spam-Ordner nach.</p>
      <Knopf art="sekundaer" gross breit onClick={props.onZurAnmeldung}>
        Zur Anmeldung
      </Knopf>
      <Knopf art="text" className="mt-2" onClick={props.onAndereAdresse}>
        Andere Adresse
      </Knopf>
    </section>
  );
}

/** Nach „Weiter“: Satz, Adresse, Gueltigkeit, „Noch mal schicken (in n s)“, „Andere Adresse“, klein das Passwort (D-344,
 *  iOS `linkUnterwegs`). Sagt nicht, ob es ein Konto gibt (der Server antwortet immer gleich). */
function LinkUnterwegs(props: {
  adresse: string;
  rest: number;
  meldung: { text: string; fehler: boolean } | null;
  laeuft: boolean;
  onNochmal: () => void;
  onPasswort: () => void;
  onAndereAdresse: () => void;
}) {
  const titel = useRef<HTMLHeadingElement>(null);
  const { rest } = props;

  useEffect(() => {
    titel.current?.focus(); // der Inhalt wechselt: Fokus auf die neue Ueberschrift
  }, []);

  return (
    // W82 Teil 4 (Vorlage R11 „anmelden“ Zustand „gesendet“): Modul auf Glas, das Symbol in der großen Linse
    <section className="link-unterwegs glas glas-modul flex flex-col items-center text-center">
      <span className="linse-gross grid size-16 place-items-center rounded-group bg-pg-accent-soft text-pg-accent-text">
        <MailCheck size={32} strokeWidth={1.5} aria-hidden />
      </span>
      {/* E1: „E-Mail“ bricht nie als „E- / Mail“; im Text und im Lesetext das normale Zeichen U+002D */}
      <h2 ref={titel} tabIndex={-1} className="t-title-2 mt-4 outline-none">
        Wir haben dir eine <span className="whitespace-nowrap">E-Mail</span> zum Anmelden geschickt.
      </h2>
      {/* Umbruch nur nach „@“; ein Teil, der allein nicht in die Zeile passt, bricht als letzter Rueckfall im Wort
          (nie abgeschnitten); bei grosser Schrift eine Stufe kleiner (bausteine.css) */}
      <p className="link-adresse t-headline mt-2 max-w-full">
        <Adresse adresse={props.adresse} />
      </p>
      <p className="t-subheadline mt-2 text-pg-text-2">Der Link gilt 15 Minuten und nur einmal. Schau auch im Spam-Ordner nach.</p>
      <p role="status" className={`t-subheadline my-4 px-4 ${props.meldung?.fehler ? "anmelde-fehler" : "text-pg-text-2"}`}>
        {props.meldung?.text ?? ""}
      </p>
      <Knopf
        art="sekundaer"
        gross
        breit
        disabled={rest > 0 || props.laeuft}
        onClick={props.onNochmal}
        aria-label={rest > 0 ? `Noch mal schicken, möglich in ${rest} Sekunden` : undefined}
      >
        {/* W73e (S7, D-382 H4): die Klammer bricht bei 320 px und 200 % nicht */}
        <span className="tabular-nums">
          {rest > 0 ? (
            <>
              Noch mal schicken <span className="whitespace-nowrap">(in {rest} s)</span>
            </>
          ) : (
            "Noch mal schicken"
          )}
        </span>
      </Knopf>
      <Knopf art="text" className="mt-2" disabled={props.laeuft} onClick={props.onAndereAdresse}>
        Andere Adresse
      </Knopf>
      <p className="mt-2">
        <KleinerWeg disabled={props.laeuft} onClick={props.onPasswort}>
          Stattdessen mit Passwort anmelden
        </KleinerWeg>
      </p>
    </section>
  );
}
